// Removed Sentry service - using Elasticsearch for logging
import { analyticsService } from './analyticsService';

export enum LogLevel {
  TRACE = 0,
  DEBUG = 1,
  INFO = 2,
  WARN = 3,
  ERROR = 4,
  FATAL = 5,
}

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: number;
  context?: Record<string, any>;
  userId?: string;
  sessionId?: string;
  url: string;
  userAgent: string;
  stack?: string;
  component?: string;
  action?: string;
}

interface LoggingConfig {
  level: LogLevel;
  enableConsole: boolean;
  enableSentry: boolean;
  enableAnalytics: boolean;
  enableLocalStorage: boolean;
  maxLocalStorageEntries: number;
  enableRemoteLogging: boolean;
  remoteEndpoint?: string;
  enableStructuredOutput: boolean;
}

class LoggingService {
  private config: LoggingConfig = {
    level: LogLevel.INFO,
    enableConsole: true,
    enableSentry: false, // Disabled Sentry integration
    enableAnalytics: false,
    enableLocalStorage: true,
    maxLocalStorageEntries: 1000,
    enableRemoteLogging: false,
    enableStructuredOutput: true,
  };

  private logs: LogEntry[] = [];
  private readonly LOG_STORAGE_KEY = 'tesseract_logs';

  initialize(config?: Partial<LoggingConfig>): void {
    this.config = { ...this.config, ...config };
    this.loadFromLocalStorage();
    
    // Set up global error handler
    this.setupGlobalErrorHandler();
    
    // Set up unhandled promise rejection handler
    this.setupUnhandledRejectionHandler();
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.config.level;
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    context?: Record<string, any>,
    component?: string,
    action?: string
  ): LogEntry {
    const entry: LogEntry = {
      level,
      message,
      timestamp: Date.now(),
      context,
      userId: analyticsService.getUserId(),
      sessionId: analyticsService.getSessionId(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      component,
      action,
    };

    // Add stack trace for errors
    if (level >= LogLevel.ERROR) {
      entry.stack = new Error().stack;
    }

    return entry;
  }

  private processLogEntry(entry: LogEntry): void {
    // Add to internal log storage
    this.logs.push(entry);

    // Limit in-memory logs
    if (this.logs.length > 1000) {
      this.logs = this.logs.slice(-500);
    }

    // Console logging
    if (this.config.enableConsole) {
      this.logToConsole(entry);
    }

    // Sentry logging disabled - using Elasticsearch instead

    // Analytics logging for significant events
    if (this.config.enableAnalytics && entry.level >= LogLevel.WARN) {
      this.logToAnalytics(entry);
    }

    // Local storage
    if (this.config.enableLocalStorage) {
      this.saveToLocalStorage();
    }

    // Remote logging
    if (this.config.enableRemoteLogging && this.config.remoteEndpoint) {
      this.logToRemote(entry);
    }
  }

  private logToConsole(entry: LogEntry): void {
    const timestamp = new Date(entry.timestamp).toISOString();
    const levelName = LogLevel[entry.level];
    
    if (this.config.enableStructuredOutput) {
      const structured = {
        timestamp,
        level: levelName,
        message: entry.message,
        component: entry.component,
        action: entry.action,
        context: entry.context,
        userId: entry.userId,
        sessionId: entry.sessionId,
      };

      switch (entry.level) {
        case LogLevel.TRACE:
        case LogLevel.DEBUG:
          console.debug('[TESSERACT]', structured);
          break;
        case LogLevel.INFO:
          console.info('[TESSERACT]', structured);
          break;
        case LogLevel.WARN:
          console.warn('[TESSERACT]', structured);
          break;
        case LogLevel.ERROR:
        case LogLevel.FATAL:
          console.error('[TESSERACT]', structured);
          if (entry.stack) {
            console.error('Stack trace:', entry.stack);
          }
          break;
      }
    } else {
      const message = `[${timestamp}] ${levelName}: ${entry.message}`;
      
      switch (entry.level) {
        case LogLevel.TRACE:
        case LogLevel.DEBUG:
          console.debug(message, entry.context);
          break;
        case LogLevel.INFO:
          console.info(message, entry.context);
          break;
        case LogLevel.WARN:
          console.warn(message, entry.context);
          break;
        case LogLevel.ERROR:
        case LogLevel.FATAL:
          console.error(message, entry.context);
          break;
      }
    }
  }

  // Removed logToSentry method - using Elasticsearch for error tracking

  private logToAnalytics(entry: LogEntry): void {
    analyticsService.track('log_event', {
      level: LogLevel[entry.level],
      message: entry.message,
      component: entry.component,
      action: entry.action,
      hasError: entry.level >= LogLevel.ERROR,
      ...entry.context,
    });
  }

  private saveToLocalStorage(): void {
    try {
      const logsToSave = this.logs.slice(-this.config.maxLocalStorageEntries);
      localStorage.setItem(this.LOG_STORAGE_KEY, JSON.stringify(logsToSave));
    } catch (error) {
      console.warn('Failed to save logs to localStorage:', error);
    }
  }

  private loadFromLocalStorage(): void {
    try {
      const stored = localStorage.getItem(this.LOG_STORAGE_KEY);
      if (stored) {
        this.logs = JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to load logs from localStorage:', error);
    }
  }

  private async logToRemote(entry: LogEntry): Promise<void> {
    if (!this.config.remoteEndpoint) return;

    try {
      await fetch(this.config.remoteEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(entry),
      });
    } catch (error) {
      console.warn('Failed to send log to remote endpoint:', error);
    }
  }

  private setupGlobalErrorHandler(): void {
    window.addEventListener('error', (event) => {
      this.error('Global Error Handler', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error?.toString(),
        stack: event.error?.stack,
      }, 'global', 'error_handler');
    });
  }

  private setupUnhandledRejectionHandler(): void {
    window.addEventListener('unhandledrejection', (event) => {
      this.error('Unhandled Promise Rejection', {
        reason: event.reason?.toString(),
        stack: event.reason?.stack,
      }, 'global', 'promise_rejection');
    });
  }

  // Public logging methods
  trace(message: string, context?: Record<string, any>, component?: string, action?: string): void {
    if (!this.shouldLog(LogLevel.TRACE)) return;
    const entry = this.createLogEntry(LogLevel.TRACE, message, context, component, action);
    this.processLogEntry(entry);
  }

  debug(message: string, context?: Record<string, any>, component?: string, action?: string): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;
    const entry = this.createLogEntry(LogLevel.DEBUG, message, context, component, action);
    this.processLogEntry(entry);
  }

  info(message: string, context?: Record<string, any>, component?: string, action?: string): void {
    if (!this.shouldLog(LogLevel.INFO)) return;
    const entry = this.createLogEntry(LogLevel.INFO, message, context, component, action);
    this.processLogEntry(entry);
  }

  warn(message: string, context?: Record<string, any>, component?: string, action?: string): void {
    if (!this.shouldLog(LogLevel.WARN)) return;
    const entry = this.createLogEntry(LogLevel.WARN, message, context, component, action);
    this.processLogEntry(entry);
  }

  error(message: string, context?: Record<string, any>, component?: string, action?: string): void {
    if (!this.shouldLog(LogLevel.ERROR)) return;
    const entry = this.createLogEntry(LogLevel.ERROR, message, context, component, action);
    this.processLogEntry(entry);
  }

  fatal(message: string, context?: Record<string, any>, component?: string, action?: string): void {
    if (!this.shouldLog(LogLevel.FATAL)) return;
    const entry = this.createLogEntry(LogLevel.FATAL, message, context, component, action);
    this.processLogEntry(entry);
  }

  // Utility methods
  getLogs(level?: LogLevel, component?: string): LogEntry[] {
    let filteredLogs = [...this.logs];

    if (level !== undefined) {
      filteredLogs = filteredLogs.filter(log => log.level >= level);
    }

    if (component) {
      filteredLogs = filteredLogs.filter(log => log.component === component);
    }

    return filteredLogs;
  }

  clearLogs(): void {
    this.logs = [];
    localStorage.removeItem(this.LOG_STORAGE_KEY);
  }

  setLevel(level: LogLevel): void {
    this.config.level = level;
  }

  getLevel(): LogLevel {
    return this.config.level;
  }

  exportLogs(format: 'json' | 'csv' = 'json'): string {
    if (format === 'json') {
      return JSON.stringify(this.logs, null, 2);
    }

    // CSV export
    const headers = ['timestamp', 'level', 'message', 'component', 'action', 'userId', 'url'];
    const csvRows = [headers.join(',')];

    this.logs.forEach(log => {
      const row = [
        new Date(log.timestamp).toISOString(),
        LogLevel[log.level],
        `"${log.message.replace(/"/g, '""')}"`,
        log.component || '',
        log.action || '',
        log.userId || '',
        log.url,
      ];
      csvRows.push(row.join(','));
    });

    return csvRows.join('\n');
  }
}

export const loggingService = new LoggingService();